import { Container, ModalTitle, Typography } from '@cleartrip/ct-design-components'
import React from 'react'

import { RightArrow } from '@/assets'
import RoundTripArrow from '@/assets/svg/RoundTripArrow'
import { CabinTabId, FareCabinTabId } from '@/constants/flights/cabinClass'
import {
    getCabinInterstitialTheme,
    isFirstClassCabinTab,
} from '@/constants/flights/premiumEconomy'
import { FareFamilyHeaderType, MetaDataType, SearchIntentType } from '@/types/srp'
import { getSearchIntentDetails } from '@/utils/srp/helper'

import CabinTabRow from '../CabinTabRow'
import PEHeaderCornerPattern from '../PEHeaderCornerPattern'
import PELegTabRow from '../PELegTabRow'

type PEInterstitialHeaderProps = {
    metaData: MetaDataType
    searchIntent: SearchIntentType
    headerDetails: FareFamilyHeaderType[]
    isRT: boolean
    isSpecialRT: boolean
    selectedTabIndex: number
    setSelectedTabIndex: (tabKey: number) => void
    selectedCabin: CabinTabId
    onCabinSelect: (cabin: CabinTabId) => void
    onPeTabRef?: (node: HTMLElement | null) => void
    priceByCabin?: Partial<Record<CabinTabId, number>>
    visibleCabins: CabinTabId[]
    /** Theme cabin for header chrome — separate from selectedCabin (sub-product tabs). */
    themeCabin?: FareCabinTabId
    /** Modal dismiss. When supplied, themed cabins render their own close control. */
    onClose?: () => void
    caretTabId?: CabinTabId
}

const PE_MODAL_TOP_BORDER_RADIUS = 12

const PE_MODAL_TITLE_PADDING_PX = 24


/**
 * Route shown in the title. Reads the ACTIVE leg's selected flight, so switching to
 * the return tab retitles the modal with the return route instead of repeating the
 * onward one (CTAIR-2568) — and each leg keeps the airport city it actually departs
 * from (e.g. Navi Mumbai), not the searched city (Mumbai). The searched origin /
 * destination remain a fallback for when the leg's airport isn't in `airportDetail`.
 */
const getRouteCityNames = (
    metaData: MetaDataType,
    searchIntent: SearchIntentType,
    headerDetails: FareFamilyHeaderType[],
    legIndex: number
) => {
    const { J1from = '', J1to = '', J2from = '', J2to = '' } = getSearchIntentDetails(searchIntent) || {}
    const isReturnLeg = legIndex === 1
    const searchedFrom = isReturnLeg ? J2from || J1to : J1from
    const searchedTo = isReturnLeg ? J2to || J1from : J1to
    const legHeader = headerDetails[legIndex] || headerDetails[0]
    const fromCity = legHeader?.from || metaData?.airportDetail?.[searchedFrom]?.city || searchedFrom
    const toCity = legHeader?.to || metaData?.airportDetail?.[searchedTo]?.city || searchedTo
    return { fromCity, toCity }
}

const CLOSE_CHIP_SIZE_PX = 32

/** White chip + dark cross, legible on both the gold and the dark First Class header. */
export const ThemedCloseButton: React.FC<{ onClose: () => void }> = ({ onClose }) => (
    <Container
        aria-label="Close"
        className="p-absolute flex flex-middle flex-center c-pointer"
        css={{
            top: `${PE_MODAL_TITLE_PADDING_PX}px`,
            right: `${PE_MODAL_TITLE_PADDING_PX}px`,
            width: `${CLOSE_CHIP_SIZE_PX}px`,
            height: `${CLOSE_CHIP_SIZE_PX}px`,
            borderRadius: '50%',
            background: '#FFFFFF',
            boxShadow: '0px 2px 8px 0px rgba(26, 26, 26, 0.24)',
            zIndex: 5,
        }}
        role="button"
        tabIndex={0}
        onClick={onClose}
        onKeyDown={(event: React.KeyboardEvent) => {
            if (event.key === 'Enter' || event.key === ' ') {
                event.preventDefault()
                onClose()
            }
        }}
    >
        <svg fill="none" height={20} viewBox="0 0 24 24" width={20} xmlns="http://www.w3.org/2000/svg">
            <path
                d="M18 6l-6 6m0 0l-6 6m6-6L6 6m6 6l6 6"
                stroke="#1A1A1A"
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={1.5}
            />
        </svg>
    </Container>
)

const RouteConnector: React.FC<{ isRT: boolean; color?: string }> = ({ isRT, color = '#1A1A1A' }) =>
    isRT ? (
        <RoundTripArrow aria-hidden className="flex-shrink-0" fill={color} />
    ) : (
        <RightArrow aria-hidden className="flex-shrink-0" height={24} style={{color: color}} width={24} />
    )

const PEInterstitialHeader: React.FC<PEInterstitialHeaderProps> = ({
    metaData,
    searchIntent,
    headerDetails,
    isRT,
    isSpecialRT,
    selectedTabIndex,
    setSelectedTabIndex,
    selectedCabin,
    onCabinSelect,
    onPeTabRef,
    priceByCabin,
    visibleCabins,
    themeCabin,
    onClose,
    caretTabId,
}) => {
    // Special RT sells the trip as one unit, so its title always states the onward route.
    const routeLegIndex = isRT && !isSpecialRT ? selectedTabIndex : 0
    const { fromCity, toCity } = getRouteCityNames(metaData, searchIntent, headerDetails, routeLegIndex)
    // Mirrors flights-pwa's InterstitialPage exactly (`isRT && !isSpecialRT`, no array-length
    // proxy) — `headerDetails.length > 1` was a fragile, indirect signal: it depends on
    // `getCardInfo`/`getFlightInfo` succeeding for the return leg, which can silently
    // resolve to a 1-length array upstream (e.g. an intl return-card selection gap) even
    // when the search is genuinely round-trip. `isRT` is the authoritative signal.
    const showLegTabs = isRT && !isSpecialRT
    // Mirrors CabinTabRow's own guard (visibleCabins.length <= 1 => renders null):
    // a single available cabin drops the tab row entirely.
    const showCabinTabs = visibleCabins.length > 1
    const themeForChrome = (themeCabin || selectedCabin) as Parameters<typeof getCabinInterstitialTheme>[0]
    const cabinTheme = getCabinInterstitialTheme(themeForChrome)
    const headerTitleColor = cabinTheme.headerTitleColor ?? '#1A1A1A'
    const isFirstClass = isFirstClassCabinTab(themeForChrome)
    // The design system's own close icon takes its colour from the light theme, so on the
    // dark First Class header it disappeared into the gold pattern; restyling it through
    // `styleConfig` did not hold up in QA (CTAIR-2557). Own the control instead whenever a
    // dismiss handler is available: hide the built-in icon and draw a white chip with a
    // dark cross, which stays legible on every cabin header.
    const useOwnCloseControl = !!onClose && cabinTheme.showHeaderPattern

    return (
        <ModalTitle
            styleConfig={{
                root: {
                    css: {
                        padding: `${PE_MODAL_TITLE_PADDING_PX}px 0 0 0`,
                        background: cabinTheme.modalHeaderBg,
                        borderTopLeftRadius: PE_MODAL_TOP_BORDER_RADIUS,
                        borderTopRightRadius: PE_MODAL_TOP_BORDER_RADIUS,
                        position: 'relative',
                        // Visible Y so the active cabin tab can overlap the row
                        // divider (marginBottom -1px). Pattern clips itself.
                        overflowX: 'hidden',
                        overflowY: 'visible',
                        // ModalTitle defaults to elevation.bottomE1 — that grey
                        // hairline sat under the selected tab on top of our rule.
                        boxShadow: 'none',
                    },
                },
                childrenTypography: {
                    css: {
                        width: '100%',
                        display: 'block',
                        flex: '1 1 100%',
                    },
                },
                iconContainer: {
                    css: useOwnCloseControl
                        ? { display: 'none' }
                        : {
                              position: 'absolute',
                              top: `${PE_MODAL_TITLE_PADDING_PX}px`,
                              right: `${PE_MODAL_TITLE_PADDING_PX}px`,
                              zIndex: 2,
                          },
                },
            }}
        >
            {cabinTheme.showHeaderPattern && (
                <PEHeaderCornerPattern variant={cabinTheme.headerPatternVariant} />
            )}
            {useOwnCloseControl && <ThemedCloseButton onClose={onClose!} />}
            <Container className="w-100p p-relative z-1">
                <Container
                    className={`flex flex-middle pl-6 pr-10 w-100p o-hidden${
                        !showLegTabs && !showCabinTabs ? ' pb-4' : ''
                    }`}
                    css={{
                        gap: '6px',
                        minWidth: 0,
                    }}
                >
                    <Typography className="flex-shrink-0 ws-nowrap" colorCode={headerTitleColor} variant="HM1">
                        Select
                    </Typography>
                    <Typography
                        className="t-truncate ws-nowrap"
                        colorCode={headerTitleColor}
                        css={{ minWidth: 0 }}
                        variant="HM1"
                    >
                        {fromCity}
                    </Typography>
                    <RouteConnector color={headerTitleColor} isRT={isRT} />
                    <Typography
                        className="t-truncate ws-nowrap"
                        colorCode={headerTitleColor}
                        css={{ minWidth: 0 }}
                        variant="HM1"
                    >
                        {toCity} fare
                    </Typography>
                </Container>

                {showLegTabs && (
                    <>
                        <PELegTabRow
                            headerDetails={headerDetails}
                            legActiveIndicatorColor={isFirstClass ? '#FFFFFF' : '#1A1A1A'}
                            legTimeColor={isFirstClass ? '#F3F3F3' : '#1A1A1A'}
                            selectedTabIndex={selectedTabIndex}
                            onSelect={setSelectedTabIndex}
                        />
                        <Container
                            className="w-100p"
                            css={{
                                height: '1px',
                                backgroundColor: isFirstClass
                                    ? 'rgba(255, 255, 255, 0.15)'
                                    : 'rgba(26, 26, 26, 0.1)',
                                flexShrink: 0,
                            }}
                        />
                    </>
                )}

                <CabinTabRow
                    caretTabId={caretTabId}
                    isRT={isRT}
                    priceByCabin={priceByCabin}
                    selectedCabin={selectedCabin}
                    themeCabin={themeCabin}
                    visibleCabins={visibleCabins}
                    onPeTabRef={onPeTabRef}
                    onSelect={onCabinSelect}
                />
            </Container>
        </ModalTitle>
    )
}

export default PEInterstitialHeader
